import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useProducts } from "../hooks/useProducts";
import type { Product } from "../types";
import { OptimizedImage } from "../utils/imageOptimization";

const fruitPatternImg = "/images/fruit-pattern.jpg";
const productDefaultBackImg = "/images/products/product-default-back.png";

interface OffersSectionProps {
  onAddToCart: (product: Product, quantity?: number) => void;
  onProductClick: (product: Product) => void;
}

export function OffersSection({
  onAddToCart,
  onProductClick,
}: OffersSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ITEM_WIDTH = 320 + 24; // min-w-[320px] + gap (space-x-6 = 24px)

  // Load products with offers from CSV
  const { offerProducts: OFFERS, isLoading } = useProducts();

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationFrameId: number;
    // Start from the middle (second set of items) for seamless left-to-right loop
    let scrollPos = scrollContainer.scrollWidth / 2;
    scrollContainer.scrollLeft = scrollPos;

    const scroll = () => {
      if (!isPaused) {
        scrollPos -= 0.4; // Move left-to-right (decrement)

        // Loop logic: when we've scrolled to the beginning, jump to middle
        if (scrollPos <= 0) {
          scrollPos = scrollContainer.scrollWidth / 2;
        }

        scrollContainer.scrollLeft = scrollPos;
      } else {
        scrollPos = scrollContainer.scrollLeft;
      }
      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);

    return () => cancelAnimationFrame(animationFrameId);
  }, [isPaused]);

  const pauseAnimation = () => {
    setIsPaused(true);

    // Clear any existing timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }

    // Resume after 10 seconds
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 10000);
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      pauseAnimation();
      scrollRef.current.scrollBy({ left: -ITEM_WIDTH, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      pauseAnimation();
      scrollRef.current.scrollBy({ left: ITEM_WIDTH, behavior: "smooth" });
    }
  };

  const handleGrabIt = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    onAddToCart(product);
  };

  const handleCardClick = (product: Product) => {
    onProductClick(product);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section className="py-12 bg-black border-y border-white/10 relative overflow-hidden">
      <div className="container mx-auto px-4 relative">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display text-4xl md:text-5xl uppercase text-white">
            Special{" "}
            <span className="text-[var(--color-sansi-orange)]">Offers</span>
          </h2>

          <div className="flex gap-2">
            <button
              onClick={scrollLeft}
              className="p-2 border border-white/20 hover:bg-[var(--color-sansi-orange)] hover:text-black hover:border-[var(--color-sansi-orange)] transition-colors rounded-full text-white"
              aria-label="Scroll left"
            >
              <ArrowLeft size={24} />
            </button>
            <button
              onClick={scrollRight}
              className="p-2 border border-white/20 hover:bg-[var(--color-sansi-orange)] hover:text-black hover:border-[var(--color-sansi-orange)] transition-colors rounded-full text-white"
              aria-label="Scroll right"
            >
              <ArrowRight size={24} />
            </button>
          </div>
        </div>

        <div
          className="flex space-x-6 overflow-x-auto md:overflow-x-hidden scrollbar-hide snap-x snap-mandatory md:snap-none"
          ref={scrollRef}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => {
            // Only resume if not manually paused via arrows
            if (!pauseTimeoutRef.current) {
              setIsPaused(false);
            }
          }}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => {
            // Resume animation after a brief delay on mobile
            setTimeout(() => {
              if (!pauseTimeoutRef.current) {
                setIsPaused(false);
              }
            }, 3000);
          }}
        >
          {/* Render items twice for infinite loop effect */}
          {isLoading ? (
            <div className="min-w-full text-center text-gray-400 py-12">
              Loading offers...
            </div>
          ) : (
            [...OFFERS, ...OFFERS].map((product, index) => (
              <div
                key={`${product.id}-${index}`}
                className="min-w-[280px] md:min-w-[320px] bg-[#1a1a1a] border border-white/10 group hover:border-[var(--color-sansi-orange)] transition-all duration-300 cursor-pointer snap-start"
                onClick={() => handleCardClick(product)}
              >
                <div className="relative aspect-video overflow-hidden border-b border-white/10 bg-white/5 flex items-center justify-center">
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-20 pointer-events-none z-0"
                    style={{
                      backgroundImage: `url(${fruitPatternImg})`,
                    }}
                  />
                  {/* Back image (shown on hover) - use default if none provided */}
                  <OptimizedImage
                    src={
                      product.backImage ||
                      productDefaultBackImg
                    }
                    alt={`${product.name} back`}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-500 opacity-0 group-hover:opacity-100 group-hover:scale-110 z-10"
                  />
                  {/* Primary image (mockup) */}
                  <OptimizedImage
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-all duration-500 group-hover:opacity-0 group-hover:scale-110 relative z-10"
                  />
                  {product.offer && (
                    <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-black px-2 py-1 uppercase flex items-center gap-1 z-10">
                      <span>{product.offer.discount}% OFF</span>
                    </div>
                  )}
                </div>

                <div className="p-4 space-y-3">
                  <h3 className="font-bold text-lg text-white uppercase line-clamp-1">
                    {product.name}
                  </h3>

                  {product.weight && (
                    <p className="text-gray-400 text-xs">{product.weight}</p>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      {product.offer && (
                        <span className="text-gray-500 text-sm line-through font-mono">
                          Rs. {product.offer.originalPrice.toLocaleString()}
                        </span>
                      )}
                      <span className="text-[var(--color-sansi-green)] font-black text-xl">
                        Rs. {product.price.toLocaleString()}
                      </span>
                    </div>

                    <button
                      onClick={(e) => handleGrabIt(e, product)}
                      className="bg-white text-black px-4 py-2 text-sm font-bold uppercase hover:bg-[var(--color-sansi-orange)] hover:text-white transition-colors"
                    >
                      Grab It
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
